<template>
  <div class="container-fluid">
    <page-header :page="page">
      <template v-slot:customActions>
        <div class="d-flex justify-content-end h-100 align-items-end">
          <ActionButton
            :class="[{ 'w-50 mr-1': isMobile, 'mb-3': !isMobile }]"
            text="Añadir"
            variant="success"
            i="fas fa-file-plus"
            @click="goAddBookCatalog"
          />
        </div>
      </template>
    </page-header>
    <filter-manager
      v-model="this.filters"
      :filters="filtersHeader"
      :open-advanced="showAdvanced"
      @subject="updateFiltersData('subject', $event)"
      @grade="updateFiltersData('grade', $event)"
      @publisher="updateFiltersData('publisher', $event)"
      @readingMedium="updateFiltersData('readingMedium', $event)"
      @search="onSearch"
    />

    <custom-separator
      text="Resultado de Libros"
      class="my-4 custom-separator-line"
    />

    <CustomResultNotFound
      v-if="showNotResults"
      class="mt-4 row pt-3"
      text="No se ha encontrado resultado de libros."
    />

    <div v-if="!showNotResults" class="search-result">
      <ResultRow
        v-for="(book, idx) in collections.books"
        :key="idx"
        ref="book"
        :value="book"
        @deleteBook="onDeleteBook"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.perPage"
        :data="pagination"
        :page-size="pagination.perPage"
        @pagination-go-page="onPageChanged"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import FilterManager from 'FilterManager';
import academicService from '@/services/api/academicUtils.api.js';
import EduEspCode from '@/utils/constants/eduEspCode';
import {
  getBooksPublishers,
  getCatalog,
  deleteBook,
} from '@/modules/books-requests/api';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow';
import sortBy from 'lodash/sortBy';
import storeMixin from '@/mixin/storeGenerator';
import alert from '@/mixin/alert.mixin';

export default {
  name: 'BooksCatalog',
  mixins: [storeMixin, alert],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
    ActionButton,
  },
  data() {
    return {
      collections: {
        messages: [],
        subjects: [],
        grades: [],
        publishers: [],
        readingMedium: [],
        books: [],
      },
      filters: {
        subject: null,
        grade: null,
        publisher: null,
        readingMedium: null,
      },
      pagination: {
        perPage: 20,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      showAdvanced: false,
    };
  },
  methods: {
    goAddBookCatalog() {
      this.$router.push('/books/BooksCatalog/Add');
    },
    updateFiltersData(key, event, callback) {
      if (event != undefined && event != null && event != '') {
        this.filters[key] = event;
      } else {
        this.filters[key] = null;
      }

      if (callback != undefined) {
        if (typeof this[callback] == 'function') {
          this[callback](event);
        }
      }
    },
    async fetchFilters() {
      const subjectsResult = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibSubject,
      });
      const readingMediumResult = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibReadingmedium,
      });

      const gradeRange = { first: 3, twelveth: 14 };
      const gradesResult = await academicService.getGrades({
        inferiorLimitId: gradeRange.first,
        superiorLimitId: gradeRange.twelveth,
      });
      const booksPublishersResult = await getBooksPublishers();
      this.collections.subjects = sortBy(subjectsResult.data, ['name']);
      this.collections.readingMediums = readingMediumResult.data;
      this.collections.grades = gradesResult.data;
      this.collections.publishers = booksPublishersResult.data;
    },
    async onSearch(filters, page = 1) {
      const mappedFilter = {
        searchField: filters.searchField,
        subjectId: (filters.subject || {}).id,
        gradeId: (filters.grade || {}).id,
        publisherId: (filters.publisher || {}).id,
        readingMediumId: (filters.readingMedium || {}).id,
        pageIndex: page,
      };

      await getCatalog(mappedFilter).then((res) => {
        this.collections.books = res.data.data;
        this.pagination = {
          ...this.pagination,
          totalItemCount: res.data.totalItemCount,
          pageCount: res.data.pageCount,
          currentPage: res.data.currentPage,
          count: res.data.count,
        };
      });
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.onSearch(this.filters, currentPage);
    },
    async onDeleteBook(book) {
      let isConfirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar el libro <b>${book.title}</b> del catálogo?`
      );
      if (isConfirmed) {
        const response = await deleteBook(book.bookId);
        if (response.status == 200) {
          this.collections.books = this.collections.books.filter(
            (x) => x.bookId != book.bookId
          );
          this.ShowToast(
            'Confirmación',
            'El libro ha sido eliminado exitosamente.',
            'success'
          );
        }
      }
    },
  },
  computed: {
    page() {
      return {
        icon: 'fas fa-book-open',
        title: 'Cátalogo de Libros',
        routes: ['Inicio', 'Libros', 'Cátalogo de Libros'],
      };
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    filtersHeader() {
      return [
        {
          name: 'subject',
          component: 'CustomDropDown',
          options: this.collections.subjects,
          value: this.filters.subject,
          placeholder: 'Materia',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-5 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.subject == null,
        },
        {
          name: 'searchField',
          component: 'CustomInput',
          placeholder: 'Escriba Autor, ISBN o Título',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-sm-12 col-md-6 col-lg-7',
          type: 'text',
        },
        {
          name: 'grade',
          label: 'Grado:',
          component: 'CustomDropDown',
          options: this.collections.grades,
          value: this.filters.grade,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-6 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.grade == null,
          isAdvanced: true,
        },
        {
          name: 'publisher',
          label: 'Casa Editorial:',
          component: 'CustomDropDown',
          options: this.collections.publishers,
          value: this.filters.publisher,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-6 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.publisher == null,
          isAdvanced: true,
        },
      ];
    },
    showNotResults() {
      return this.collections.books.length == 0;
    },
  },
  async mounted() {
    await this.fetchFilters();

    this.showAdvanced = this.filtersHeader.some(
      (filter) => filter.value != null && filter.isAdvanced
    );
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = [
      'AddBooksCatalog',
      'ReadBooksCatalog',
      'EditBooksCatalog',
    ];

    next((vm) => {
      if (persistRoutes.some((x) => x == from.name)) {
        vm.recoveryData();
        vm.onSearch(vm.filters, vm.pagination.currentPage);
      } else {
        vm.shouldRecovery = false;
        vm.showAdvanced = false;
        vm.filters = {};
        vm.collections.books = [];
      }
    });
  },
};
</script>

<style scoped>
@media only screen and (max-width: 375px) {
  ::v-deep .separator-custom__text {
    font-size: 0.75rem !important;
    font-weight: normal;
    padding: 2px 20px !important;
  }
}
</style>
